/**
 * Who generated a draft, and when, read off its version history. Section
 * regenerates and Casey's save-backs are generations too, but they rewrite
 * part of a document someone else wrote, so they do not count here.
 */
import type { DraftVersion } from "@/api/types/draft";

import { getDisplayName, getRequesterName } from "./versionLabels";

const GENERATED_TRIGGERS: ReadonlySet<string> = new Set([
  "full_generate",
  "agent_generate",
  "legacy_generate",
  "exhibit_list_render"
]);

export const isGeneratedVersion = (v: DraftVersion): boolean =>
  GENERATED_TRIGGERS.has(String(v.trigger));

/** The newest version that generated the document; versions are newest first. */
export const latestGeneration = (
  versions: readonly DraftVersion[]
): DraftVersion | null => versions.find(isGeneratedVersion) ?? null;

/** "Nov 2, 2026", or null when the date does not parse. */
export const formatProvenanceDate = (
  iso: string | null | undefined
): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

/**
 * Who the line names. Casey and CaseBlink stay themselves. A drafting-agent
 * generation names the person who asked (`created_by_profile`, from
 * `created_by`) when that version stored one, and stays "Drafting Agent"
 * when it did not. A uid with no name is not that person.
 */
const generatedBy = (v: DraftVersion): string => {
  if (v.actor_kind === "drafting_agent") {
    return getRequesterName(v) ?? "Drafting Agent";
  }
  return getDisplayName(v);
};

/** "Generated Nov 2, 2026 by Casey". */
export const generatedLine = (v: DraftVersion): string => {
  const date = formatProvenanceDate(v.created_at);
  const by = generatedBy(v);
  return date ? `Generated ${date} by ${by}` : `Generated by ${by}`;
};
